import React from 'react';
import { Row } from 'vetrf-ui/dist/utils/type/profile-table/Row';
import { EntryType } from 'components/registry/types/api';
import { CountryType } from 'components/registry/components/CountryType/CountryType';
import { AnimalSpeciesDictOption } from 'api/dict/params';
import { ErulRegistrationCodes } from 'api/erul/types';
import { CerberusIntegrationStatus } from 'components/CerberusIntegrationStatus';
import { CerberusIntegrationError } from 'components/CerberusIntegrationError';
import { getMessageForElkEpgu } from 'api/elkEpgu/utils';
import { ProductDto } from '../../../../../../../feedRegistry/api/params';
import { formatVendor } from '../../../../../../../utils';
import { InformationTable } from './types';

export const mainBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.name,
    header: {
      key: 'name',
      title: 'Торговое наименование',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.form,
    header: {
      key: 'form',
      title: 'Форма',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.purpose,
    header: {
      key: 'purpose',
      title: 'Назначение',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.composition,
    header: {
      key: 'composition',
      title: 'Состав',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.consumerPackageSize,
    header: {
      key: 'consumerPackageSize',
      title: 'Объем или масса в упаковке',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.periodOfValidity,
    header: {
      key: 'periodOfValidity',
      title: 'Срок годности',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.storageCondition,
    header: {
      key: 'storageCondition',
      title: 'Условия хранения',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      item.feedAdditiveTypeList.map((type: { code: string; description: string }) => (
        <div key={type.code}>
          {type.code}: {type.description}
        </div>
      )),
    header: {
      key: 'feedAdditiveTypeList',
      title: 'Тип',
    },
    isRegisteredRequest: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      item.feedAdditiveKindList.map((kind: { code: string; description: string }) => (
        <div key={kind.code}>
          {kind.code}: {kind.description}
        </div>
      )),
    header: {
      key: 'feedAdditiveKindList',
      title: 'Вид',
    },
    access: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      item.animalSpeciesList
        .map((animal: { animalSpecies: AnimalSpeciesDictOption }) => animal.animalSpecies.name)
        .join(', '),
    header: {
      key: 'animalSpeciesList',
      title: 'Вид животного',
    },
    mode: [EntryType.requests, EntryType.registry],
    access: true,
  },
];

export const developerBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => (
      <>
        <CountryType isResident={item.vendorType === 'DOMESTIC'} />
        <span style={{ marginLeft: '8px' }}>{formatVendor(item as ProductDto, true)}</span>
        <CerberusIntegrationStatus
          isEnteredManually={item.vendorEnteredManually}
          isNotLinkedWithCerberus={item.vendorNotLinkedWithCerberus}
        />
        <CerberusIntegrationError showMessage={item.vendorIncorrectCerberusLink} />
      </>
    ),
    header: {
      key: 'vendor',
      title: 'Разработчик',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const stateBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.series,
    header: {
      key: 'series',
      title: 'Учетная серия',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.regNumber,
    header: {
      key: 'regNumber',
      title: 'Регистрационный номер',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.startValidityDate,
    header: {
      key: 'startValidityDate',
      title: 'Дата государственной регистрации',
    },
    mode: [EntryType.registry],
  },
  {
    dataFormatter: (item) => item.requestDate,
    header: {
      key: 'requestDate',
      title: 'Дата регистрации заявки',
    },
    isRegisteredRecord: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.endValidityDate || 'бессрочно',
    header: {
      key: 'endValidityDate',
      title: 'Срок государственной регистрации',
    },
    mode: [EntryType.registry],
  },
  {
    dataFormatter: (item) => item.registryStatus,
    header: {
      key: 'registryStatus',
      title: 'Статус',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const requestBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.requestRegNumber,
    header: {
      key: 'requestRegNumber',
      title: 'Номер заявления',
    },
    isRegisteredRecord: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.requestRegDate,
    header: {
      key: 'requestRegDate',
      title: 'Дата регистрации заявления',
    },
    isRegisteredRecord: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.requestMethod?.name,
    header: {
      key: 'requestMethod',
      title: 'Способ подачи заявления',
    },
    isRegisteredRecord: true,
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const epguRegistrationBlockColumn: Row & {
  mode: EntryType[];
  isRegisteredRecord?: boolean;
} = {
  dataFormatter: (item) => getMessageForElkEpgu(item.epguRegistration),
  header: {
    key: 'epguRegistration',
    title: 'Передача в ЕЛК ЕПГУ статуса заявления',
  },
  isRegisteredRecord: true,
  mode: [EntryType.requests, EntryType.registry],
};

export const erulBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.erulRegNumber,
    header: {
      key: 'erulRegNumber',
      title: 'Регистрационный номер ЕРУЛ',
    },
    isUserData: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.erulRegDate,
    header: {
      key: 'erulRegDate',
      title: 'Дата получения регистрационного номера ЕРУЛ',
    },
    isUserData: true,
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const erulWithStatusBlockColumns: InformationTable = [
  {
    dataFormatter: (item) =>
      !!item.erulRegNumber &&
      `${item.erulRegNumber}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'erulRegNumber',
      title: 'Регистрационный номер ЕРУЛ',
    },
    isUserData: true,
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      !!item.erulRegDate &&
      `${item.erulRegDate}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'erulRegDate',
      title: 'Дата получения регистрационного номера ЕРУЛ',
    },
    isUserData: true,
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const getGMOBlockColumns = (entryType: EntryType): Row[] => [
  {
    dataFormatter: (item) =>
      item.gmoList?.length ? (
        <>
          {item.gmoList.map((gmo: { regNumber: string }) => (
            <div key={gmo.regNumber}>Регистрационный номер: {gmo.regNumber}</div>
          ))}
        </>
      ) : (
        entryType === EntryType.registry && 'Не содержит ГМО'
      ),
    header: {
      key: 'gmo',
      title: 'Сведения о ГМО',
    },
  },
];
